/**
 * The "Who is coming?" answer on spa bookings, as it travels on a reservation.
 *
 * The booking flow writes it as the first line of the reservation notes
 * ("Guests: 1 woman, 1 man"), so it needs no column of its own. The admin
 * reads it back from there to show Women / Men on the reservation and in the
 * WhatsApp copy, and shows only the rest of the notes as Special requests.
 * Writer and reader live here so the two can never drift apart.
 */
const PREFIX = "Guests: ";

export interface GuestMixCounts {
  women: number;
  men: number;
}

/** "Guests: 2 women, 1 man". Kept in English: it is read by staff. */
export function formatGuestMixLine({ women, men }: GuestMixCounts): string {
  const parts: string[] = [];
  if (women > 0) parts.push(`${women} ${women === 1 ? "woman" : "women"}`);
  if (men > 0) parts.push(`${men} ${men === 1 ? "man" : "men"}`);
  return `${PREFIX}${parts.join(", ")}`;
}

/** Notes without any guest line, trimmed. */
export function stripGuestMixLine(notes: string | null | undefined): string {
  return (notes ?? "")
    .split("\n")
    .filter((line) => !line.startsWith(PREFIX))
    .join("\n")
    .trim();
}

/**
 * The counts in a reservation's notes, and what the guest typed besides.
 * Null when the notes carry no guest line (every booking that is not a spa,
 * and spa bookings made before the question existed).
 */
export function parseGuestMixNote(
  notes: string | null | undefined,
): (GuestMixCounts & { rest: string }) | null {
  const line = (notes ?? "").split("\n").find((l) => l.startsWith(PREFIX));
  if (!line) return null;
  const count = (re: RegExp) => Number(line.match(re)?.[1] ?? 0);
  return {
    women: count(/(\d+) wom[ae]n/),
    men: count(/(\d+) m[ae]n\b(?<!wom[ae]n)/),
    rest: stripGuestMixLine(notes),
  };
}

/**
 * The spas that take a man on his own (owner, 2026-09-30: "Mandara Thai
 * Massage is the only spa that accepts a man alone; all other spas must be a
 * woman or a couple"; Mawli Spa added 2026-10-15, same answer from the owner).
 * Yoma Spa added 2026-10-25: its foot Thai salon takes men from 10:00 to
 * 02:00, and its spa is open to everyone after its 10:00 to 16:00 women-only
 * hours (the venue, by WhatsApp). Our team checks the hour when confirming.
 * An allow-list on purpose: a spa added later defaults to women and couples.
 */
export const MEN_ALONE_SPA_SLUGS: ReadonlySet<string> = new Set([
  "mandara-thai-massage-marrakech",
  "mawli-spa-marrakech",
  "yoma-spa-marrakech",
]);

/** Spa bookings ask who is coming. Keyed off the category slug. */
export function isSpaCategory(categorySlug: string | null | undefined): boolean {
  return (categorySlug ?? "").trim().toLowerCase() === "spas";
}

/**
 * True when this spa turns the group away: nobody in it is a woman. A group of
 * five men walked into Marajah on 2026-10-04 off a booking typed in by hand,
 * so the admin forms and the API both check this, not only the website.
 */
export function guestMixRefusedBy(slug: string, counts: GuestMixCounts): boolean {
  return !MEN_ALONE_SPA_SLUGS.has(slug) && counts.men > 0 && counts.women === 0;
}
